// Growable little-endian binary writer for the protocol's primitive types (protocol §2).
//
// Integer writers throw RangeError for values that do not fit their type: that is always a bug in
// the caller, never bad input from the network. `pos` and `rad` clamp to their range instead, and
// `str` truncates at a character boundary, because those carry world data that may legitimately
// sit at an edge.

const INITIAL_CAPACITY = 256;
const I16_MIN = -32768;
const I16_MAX = 32767;
const U16_MAX = 65535;

/** Number of bytes `vu` needs for `v` (1–5). */
export function varintLength(v: number): number {
  return v < 0x80 ? 1 : v < 0x4000 ? 2 : v < 0x200000 ? 3 : v < 0x10000000 ? 4 : 5;
}

/** Largest value a `vl` may carry: 2⁵³ − 1 (`Number.MAX_SAFE_INTEGER`, protocol §2). */
export const VL_MAX_VALUE = Number.MAX_SAFE_INTEGER;
/** Longest legal `vl`: 8 bytes (7 bits each covers 53 bits). */
export const VL_MAX_BYTES = 8;

/** Number of bytes `vl` needs for `v` (1–8). */
export function vlLength(v: number): number {
  let n = 1;
  let x = v;
  while (x >= 0x80) {
    x = Math.floor(x / 0x80);
    n++;
  }
  return n;
}

/** UTF-8 byte length of `s` (lone surrogates count as U+FFFD, 3 bytes, as TextEncoder does). */
export function utf8Length(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (
      c >= 0xd800 &&
      c <= 0xdbff &&
      i + 1 < s.length &&
      isLowSurrogate(s.charCodeAt(i + 1))
    ) {
      n += 4;
      i++;
    } else n += 3;
  }
  return n;
}

function isLowSurrogate(c: number): boolean {
  return c >= 0xdc00 && c <= 0xdfff;
}

/**
 * Finds how much of `s` fits in `maxBytes` of UTF-8 without splitting a character.
 * Returns the UTF-16 end index and the byte length of `s.slice(0, end)`.
 */
function utf8Fit(s: string, maxBytes: number): { end: number; bytes: number } {
  let n = 0;
  let i = 0;
  while (i < s.length) {
    const c = s.charCodeAt(i);
    let len: number;
    let units = 1;
    if (c < 0x80) len = 1;
    else if (c < 0x800) len = 2;
    else if (
      c >= 0xd800 &&
      c <= 0xdbff &&
      i + 1 < s.length &&
      isLowSurrogate(s.charCodeAt(i + 1))
    ) {
      len = 4;
      units = 2;
    } else len = 3;
    if (n + len > maxBytes) break;
    n += len;
    i += units;
  }
  return { end: i, bytes: n };
}

export class Writer {
  private buf: Uint8Array<ArrayBuffer>;
  private dv: DataView;
  private len = 0;

  constructor(initialCapacity: number = INITIAL_CAPACITY) {
    this.buf = new Uint8Array(Math.max(16, initialCapacity));
    this.dv = new DataView(this.buf.buffer);
  }

  /** Bytes written so far. */
  get length(): number {
    return this.len;
  }

  /** Forgets everything written, keeping the allocated buffer for reuse. */
  reset(): this {
    this.len = 0;
    return this;
  }

  /** A copy of the bytes written so far (safe to keep or send). */
  finish(): Uint8Array<ArrayBuffer> {
    return this.buf.slice(0, this.len);
  }

  /** A view of the bytes written so far, without copying. Invalid after the next write or reset. */
  view(): Uint8Array<ArrayBuffer> {
    return this.buf.subarray(0, this.len);
  }

  /** Truncates back to `length` bytes (e.g. to drop a record that would pass a budget). */
  truncate(length: number): this {
    if (length < 0 || length > this.len) throw new RangeError(`truncate(${length}) out of range`);
    this.len = length;
    return this;
  }

  private ensure(extra: number): void {
    const need = this.len + extra;
    if (need <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < need) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf.subarray(0, this.len));
    this.buf = next;
    this.dv = new DataView(next.buffer);
  }

  u8(v: number): this {
    if ((v & 0xff) !== v) throw new RangeError(`u8 out of range: ${v}`);
    this.ensure(1);
    this.buf[this.len++] = v;
    return this;
  }

  u16(v: number): this {
    if ((v & 0xffff) !== v) throw new RangeError(`u16 out of range: ${v}`);
    this.ensure(2);
    this.dv.setUint16(this.len, v, true);
    this.len += 2;
    return this;
  }

  u32(v: number): this {
    if (v >>> 0 !== v) throw new RangeError(`u32 out of range: ${v}`);
    this.ensure(4);
    this.dv.setUint32(this.len, v, true);
    this.len += 4;
    return this;
  }

  i8(v: number): this {
    if ((v << 24) >> 24 !== v) throw new RangeError(`i8 out of range: ${v}`);
    this.ensure(1);
    this.dv.setInt8(this.len, v);
    this.len += 1;
    return this;
  }

  i16(v: number): this {
    if ((v << 16) >> 16 !== v) throw new RangeError(`i16 out of range: ${v}`);
    this.ensure(2);
    this.dv.setInt16(this.len, v, true);
    this.len += 2;
    return this;
  }

  /** IEEE-754 single. Throws on NaN or ±Infinity, which a receiver would reject (protocol §2). */
  f32(v: number): this {
    if (!Number.isFinite(v)) throw new RangeError(`f32 must be finite: ${v}`);
    this.ensure(4);
    this.dv.setFloat32(this.len, v, true);
    this.len += 4;
    return this;
  }

  /** Unsigned LEB128 varint, 0 to 2³² − 1 (protocol §2). */
  vu(v: number): this {
    if (v >>> 0 !== v) throw new RangeError(`vu out of range: ${v}`);
    this.ensure(5);
    let x = v;
    while (x >= 0x80) {
      this.buf[this.len++] = (x & 0x7f) | 0x80;
      x >>>= 7;
    }
    this.buf[this.len++] = x;
    return this;
  }

  /**
   * `vl`: unsigned LEB128 varint for integers up to 2⁵³ − 1 (protocol §2), 1–8 bytes. Used for
   * lamport amounts and leaderboard amounts. Values up to 2³² − 1 encode exactly like `vu`.
   */
  vl(v: number): this {
    if (!Number.isSafeInteger(v) || v < 0) throw new RangeError(`vl out of range: ${v}`);
    this.ensure(VL_MAX_BYTES);
    let x = v;
    while (x >= 0x80) {
      this.buf[this.len++] = (x % 0x80) | 0x80;
      x = Math.floor(x / 0x80);
    }
    this.buf[this.len++] = x;
    return this;
  }

  /**
   * `str`: UTF-8 byte length as `vu`, then the bytes (protocol §2). A string longer than
   * `maxBytes` is truncated at a character boundary; lone surrogates are written as U+FFFD.
   */
  str(s: string, maxBytes: number): this {
    const fit = utf8Fit(s, maxBytes);
    this.vu(fit.bytes);
    this.ensure(fit.bytes);
    const buf = this.buf;
    let p = this.len;
    for (let i = 0; i < fit.end; i++) {
      let c = s.charCodeAt(i);
      if (c < 0x80) {
        buf[p++] = c;
      } else if (c < 0x800) {
        buf[p++] = 0xc0 | (c >> 6);
        buf[p++] = 0x80 | (c & 0x3f);
      } else if (
        c >= 0xd800 &&
        c <= 0xdbff &&
        i + 1 < fit.end &&
        isLowSurrogate(s.charCodeAt(i + 1))
      ) {
        const cp = 0x10000 + ((c - 0xd800) << 10) + (s.charCodeAt(i + 1) - 0xdc00);
        i++;
        buf[p++] = 0xf0 | (cp >> 18);
        buf[p++] = 0x80 | ((cp >> 12) & 0x3f);
        buf[p++] = 0x80 | ((cp >> 6) & 0x3f);
        buf[p++] = 0x80 | (cp & 0x3f);
      } else {
        if (c >= 0xd800 && c <= 0xdfff) c = 0xfffd; // lone surrogate
        buf[p++] = 0xe0 | (c >> 12);
        buf[p++] = 0x80 | ((c >> 6) & 0x3f);
        buf[p++] = 0x80 | (c & 0x3f);
      }
    }
    this.len = p;
    return this;
  }

  /** `rgb`: three bytes red, green, blue from a 0xRRGGBB colour. */
  rgb(c: number): this {
    if ((c & 0xffffff) !== c) throw new RangeError(`rgb out of range: ${c}`);
    this.ensure(3);
    this.buf[this.len++] = (c >> 16) & 0xff;
    this.buf[this.len++] = (c >> 8) & 0xff;
    this.buf[this.len++] = c & 0xff;
    return this;
  }

  /** `pos`: `round(value / posQ)` as i16, clamped to the i16 range (protocol §2). */
  pos(value: number, posQ: number): this {
    return this.posQ(Math.round(value / posQ));
  }

  /** `pos` from an already quantised integer, clamped to the i16 range. */
  posQ(q: number): this {
    if (Number.isNaN(q)) throw new RangeError('pos is NaN');
    return this.i16(q < I16_MIN ? I16_MIN : q > I16_MAX ? I16_MAX : q);
  }

  /** `rad`: `round(r / radQ)` as u16, clamped to 0–65535 (protocol §2). */
  rad(r: number, radQ: number): this {
    return this.radQ(Math.round(r / radQ));
  }

  /** `rad` from an already quantised integer, clamped to 0–65535. */
  radQ(q: number): this {
    if (Number.isNaN(q)) throw new RangeError('rad is NaN');
    return this.u16(q < 0 ? 0 : q > U16_MAX ? U16_MAX : q);
  }

  /** Appends raw bytes (e.g. a cached, pre-encoded record; protocol §6.4). */
  bytes(src: Uint8Array): this {
    this.ensure(src.length);
    this.buf.set(src, this.len);
    this.len += src.length;
    return this;
  }

  /**
   * Appends `src[start … end)` without creating a view of it (short runs are copied byte by byte,
   * which is cheaper than `subarray` + `set` for the few-byte records of a FRAME).
   */
  bytesRange(src: Uint8Array, start: number, end: number): this {
    const n = end - start;
    if (n <= 0) return this;
    this.ensure(n);
    const buf = this.buf;
    if (n <= 32) {
      let p = this.len;
      for (let i = start; i < end; i++) buf[p++] = src[i] as number;
    } else {
      buf.set(src.subarray(start, end), this.len);
    }
    this.len += n;
    return this;
  }

  /** Appends everything written to `other` so far (no intermediate view). */
  append(other: Writer): this {
    return this.bytesRange(other.buf, 0, other.len);
  }

  /** Reserves four bytes for a `u32` written later with `patchU32`. Returns their offset. */
  reserveU32(): number {
    this.ensure(4);
    const mark = this.len;
    this.dv.setUint32(mark, 0, true);
    this.len += 4;
    return mark;
  }

  patchU32(mark: number, v: number): this {
    if (v >>> 0 !== v) throw new RangeError(`u32 out of range: ${v}`);
    if (mark < 0 || mark + 4 > this.len) throw new RangeError(`bad mark ${mark}`);
    this.dv.setUint32(mark, v, true);
    return this;
  }

  /**
   * Hands over the bytes written so far: returns the underlying ArrayBuffer and its used length,
   * and continues with a fresh buffer of `nextCapacity` bytes (the returned one is never touched
   * again by this writer, so it may be transferred or kept). Returns null when nothing was written.
   */
  detach(nextCapacity?: number): { readonly buf: ArrayBuffer; readonly byteLength: number } | null {
    if (this.len === 0) return null;
    const out = { buf: this.buf.buffer, byteLength: this.len };
    this.buf = new Uint8Array(Math.max(16, nextCapacity ?? this.buf.length));
    this.dv = new DataView(this.buf.buffer);
    this.len = 0;
    return out;
  }

  /** Reserves one byte for a `u8` written later with `patchU8`. Returns its offset. */
  reserveU8(): number {
    this.ensure(1);
    this.buf[this.len] = 0;
    return this.len++;
  }

  patchU8(mark: number, v: number): this {
    if ((v & 0xff) !== v) throw new RangeError(`u8 out of range: ${v}`);
    if (mark < 0 || mark >= this.len) throw new RangeError(`bad mark ${mark}`);
    this.buf[mark] = v;
    return this;
  }

  /**
   * Reserves room for a `vu` whose value is not known yet (a count or a section length).
   * One byte is reserved; `patchVu` shifts everything after it if the value needs more.
   * Patch nested reservations innermost first (last reserved, first patched).
   */
  reserveVu(): number {
    this.ensure(1);
    this.buf[this.len] = 0;
    return this.len++;
  }

  /** Writes the varint reserved at `mark`, shifting the bytes after it as needed. */
  patchVu(mark: number, v: number): this {
    if (v >>> 0 !== v) throw new RangeError(`vu out of range: ${v}`);
    if (mark < 0 || mark >= this.len) throw new RangeError(`bad mark ${mark}`);
    const n = varintLength(v);
    if (n > 1) {
      this.ensure(n - 1);
      this.buf.copyWithin(mark + n, mark + 1, this.len);
      this.len += n - 1;
    }
    let p = mark;
    let x = v;
    while (x >= 0x80) {
      this.buf[p++] = (x & 0x7f) | 0x80;
      x >>>= 7;
    }
    this.buf[p] = x;
    return this;
  }
}
